// Big J — a walkable 1:1 voxel USS New Jersey with a working main battery.
// This file is the glue: scene rig, the shared G context every system
// hangs off, input routing, the frame loop, and the mission select. The
// scenarios themselves (WWII surface action, the Vietnam gunline, Beirut)
// live in missions.js.

import * as THREE from "three";
import { WATERLINE_Y, KTS } from "./const.js";
import { buildShip } from "./builder.js";
import { gridToMeshes } from "./voxel.js";
import { makeSea } from "./sea.js";
import { Player } from "./player.js";
import { Particles, funnelSmoke, bowSpray } from "./effects.js";
import * as fx from "./effects.js";
import { Audio } from "./audio.js";
import { Guns } from "./guns.js";
import { FireControl } from "./firecontrol.js";
import { Radar } from "./radar.js";
import { PlotBoard } from "./plotboard.js";
import { StarShells } from "./starshell.js";
import { Torpedoes } from "./torpedo.js";
import { Barks } from "./barks.js";
import { Damage } from "./damage.js";
import { Crew } from "./crew.js";
import { Stations } from "./stations.js";
import { Hud } from "./hud.js";
import { Waypoint } from "./waypoint.js";
import { MISSIONS } from "./missions.js";
import { initTouch, enterMobileFullscreen } from "./touch.js";

const canvas = document.getElementById("game");
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(72, 1, 0.1, 90000);
const sea = makeSea(scene);

// --- ship rig ---------------------------------------------------------------
const shipGroup = new THREE.Group();
const shipInner = new THREE.Group();
shipInner.position.set(-137, -WATERLINE_Y, -17.5);
shipGroup.add(shipInner);
scene.add(shipGroup);

const shipState = {
  heading: 0.35, speed: 8 * KTS, orderedSpeed: 8 * KTS,
  rudder: 0, orderedRudder: 0, rudderJammed: false,
  pos: new THREE.Vector2(0, 0),
  list: 0, trim: 0, speedCap: 33, recoil: 0, engineManned: 0,
};

const ship = buildShip();
shipInner.add(gridToMeshes(ship.grid));
shipInner.add(ship.ladderMesh);
for (const t of ship.turrets) shipInner.add(t.group);
shipInner.add(ship.directorMesh, ship.aftDirectorMesh, ship.skRadar, ship.sgRadar);

const player = new Player(ship.grid, ship.spawn);
shipInner.add(camera);

// --- shared context ----------------------------------------------------------
const G = {
  THREE, scene, camera, renderer,
  shipGroup, shipInner, shipState, ship, player,
  sea,
  gq: false, hostile: true, over: false, night: false,
  fx,
  mission: null,
  terrain: null,
  enemyShips: [],
  shore: [],
  enemies: () => G.enemyShips,
  targets: () => [...G.enemyShips, ...G.shore],
  shipWorldPos: () => shipGroup.position,
  cameraWorld: () => camera.getWorldPosition(new THREE.Vector3()),
  shake: (m) => {
    shake = Math.min(2, shake + m);
    // every kinetic moment funnels through here — on a phone it's a thump
    // in the hand, scaled to the blow
    G.buzz?.(Math.min(140, Math.round(m * 90)));
  },
  roomAt(p) {
    for (const r of ship.rooms) {
      if (p.x >= r.x0 && p.x <= r.x1 && p.z >= r.z0 && p.z <= r.z1 && Math.abs(p.y - r.walk) <= 2) return r;
    }
    return null;
  },
  gameOver,
  soundGQ,
};

G.worldFx = new Particles(scene);
G.shipFx = new Particles(shipInner);
G.audio = new Audio();
G.hud = new Hud(G);
G.damage = new Damage(G);
G.crew = new Crew(G);
G.guns = new Guns(G);
G.fc = new FireControl(G);
G.radar = new Radar(G);
G.plotBoard = new PlotBoard(G);
G.stations = new Stations(G);
G.waypoint = new Waypoint(G);
G.illum = new StarShells(G);
G.torps = new Torpedoes(G);
G.barks = new Barks(G);

// Night missions douse the sun and dim the (unlit, baked-light) voxel ship
// to match — but only the weather surfaces. The interior faces live in
// their own "vox-interior" meshes and keep their baked lamp light: darken
// ship is a topside order, and a sailor below decks still works under the
// battle lanterns. The scopes and the plotting board keep their glow too.
G.setNight = () => {
  G.night = true;
  sea.setNight();
  const tint = new THREE.Color(0x525c74);
  shipInner.traverse((o) => {
    if (o.isMesh && (o.name === "vox-solid" || o.name === "vox-glass" || o.name === "ladders")) {
      o.material.color.copy(tint);
    }
    // The crew is Lambert-lit and a moonless sky gives them nothing — but a
    // sailor below decks stands under the same lamps as the compartment
    // around him. Half-strength self-light keeps them people, not voids.
    if (o.isMesh && o.material?.isMeshLambertMaterial) {
      o.material.emissive.copy(o.material.color).multiplyScalar(0.55);
    }
  });
};

// CIC console gets a live PPI screen in 3D
const screen = G.radar.makeScreenMesh();
screen.position.set(152.5, 18.1, 12.01);
shipInner.add(screen);

// Main battery plot gets the live time-range plotting board on the forward
// bulkhead — the rangekeeper crew's actual working surface.
const board = G.plotBoard.makeScreenMesh(3.6, 1.9);
board.position.set(134.5, 9.02, 6.03);
shipInner.add(board);

// --- mission select / start -----------------------------------------------------
let orders = [];
let shake = 0;
let started = false;
const stats = { t0: 0 };

function buildMissionCards() {
  const box = document.getElementById("bb-missions");
  box.innerHTML = MISSIONS.map((m, i) => `
    <div class="bb-mission" data-i="${i}" tabindex="0">
      <h3>${m.title}</h3>
      <div class="bb-mtag">${m.tag}</div>
      <p>${m.blurb}</p>
      <span class="bb-mgo">REPORT ABOARD →</span>
    </div>`).join("");
  for (const el of box.querySelectorAll(".bb-mission")) {
    el.addEventListener("click", () => start(MISSIONS[+el.dataset.i]));
  }
}
buildMissionCards();

function start(mission) {
  if (started) return;
  started = true;
  document.getElementById("bb-title").classList.add("hidden");
  document.getElementById("bb-cross").classList.remove("hidden");
  document.body.classList.add("playing");
  // Desktop captures the mouse; a phone goes fullscreen and lies down in
  // landscape instead — the closest thing the web has to "the game has
  // your screen now".
  if (G.touch) enterMobileFullscreen();
  else canvas.requestPointerLock();
  G.audio.ensure();
  G.mission = mission;
  orders = mission.orders().map((o) => ({ ...o, done: false }));
  G.hud.setOrders(orders, mission.ordersTitle);
  mission.setup(G);
  stats.t0 = performance.now();
}

function soundGQ(on, auto) {
  G.gq = on;
  G.crew.soundGQ(on);
  if (on) {
    G.audio.klaxon(3);
    G.buzz?.([90, 70, 90, 70, 90]); // the klaxon in your palm
    G.hud.log(auto
      ? "GENERAL QUARTERS, GENERAL QUARTERS — ALL HANDS MAN YOUR BATTLE STATIONS"
      : "GENERAL QUARTERS SOUNDED", "bad");
  } else G.hud.log("SECURE FROM GENERAL QUARTERS. CONDITION III SET.");
}

function gameOver(won, text, title) {
  if (G.over) return;
  G.over = true;
  const mins = (performance.now() - stats.t0) / 60000;
  const fc = G.fc;
  if (won) {
    const shoreN = G.shore.length;
    const bda = shoreN ? ` · ${G.shore.filter((t) => t.destroyed).length} of ${shoreN} targets destroyed` : "";
    G.hud.banner(title || "MISSION COMPLETE",
      `${fc.salvosFired} salvos · ${fc.straddles} straddles · ${fc.hits} hits${bda} · ${mins.toFixed(1)} minutes`
      + (G.damage.hitsTaken ? ` · armor shrugged ${G.damage.shellsShrugged} of ${G.damage.hitsTaken} hits` : "")
      + gradeMission(mins));
  } else {
    G.hud.banner(title || "THE BIG J IS LOST", text || "", true);
    if (!title) shipState.sinking = true;
  }
}

// Post-action gunnery grade, Navy style: accuracy, dispatch, and the state
// you brought the ship home in. Ship's records live in localStorage per
// mission — the Battle Efficiency "E" is something to defend.
function gradeMission(mins) {
  const fc = G.fc, d = G.damage;
  const salvos = Math.max(1, fc.salvosFired);
  const par = G.mission?.par ?? 15;
  const accScore = 45 * Math.min(1, (fc.straddles + fc.hits) / salvos);
  const speedScore = 25 * Math.max(0, Math.min(1, 2 - mins / par));
  const dmgHits = Math.max(0, d.hitsTaken - d.shellsShrugged);
  const condScore = 30 * Math.max(0, 1 - dmgHits * 0.12 - d.floodTotal * 0.06);
  const score = Math.round(accScore + speedScore + condScore);
  const grade = score >= 85 ? "E" : score >= 70 ? "A" : score >= 55 ? "B" : score >= 40 ? "C" : "D";
  const gradeName = grade === "E" ? "BATTLE EFFICIENCY “E”" : `GRADE ${grade}`;
  let recordLine = "";
  try {
    const key = `bigj-best-${G.mission?.id ?? "x"}`;
    const best = JSON.parse(localStorage.getItem(key) || "null");
    if (!best || score > best.score) {
      localStorage.setItem(key, JSON.stringify({ score, grade, mins: +mins.toFixed(1) }));
      recordLine = best ? ` — NEW SHIP'S RECORD (was ${best.grade} · ${best.score})` : " — SHIP'S RECORD SET";
    } else {
      recordLine = ` — ship's record: ${best.grade} · ${best.score} · ${best.mins} min`;
    }
  } catch { /* private browsing etc. — no records, no problem */ }
  return `<br/><b>${gradeName} — ${score}/100</b>` +
    ` (gunnery ${Math.round(accScore)}/45 · dispatch ${Math.round(speedScore)}/25 · ship's condition ${Math.round(condScore)}/30)` +
    recordLine;
}

// Salvo camera: ride the last round out and watch your own splash land.
// Time compresses in the cruise phase so a 60-second time of flight doesn't
// feel like one; the camera lingers on the burst, then puts you back aboard.
const shellCam = { on: false, shell: null, linger: 0 };
G.shellCam = shellCam;
function toggleShellCam() {
  if (shellCam.on) { shellCam.on = false; return; }
  const live = G.guns.shells.filter((s) => s.own && !s.dead);
  if (!live.length) { G.hud.log("NO ROUNDS IN THE AIR"); return; }
  shellCam.shell = live[live.length - 1];
  shellCam.on = true;
  shellCam.linger = 3.2;
}
G.followSalvo = toggleShellCam;

// Teleport: whisk the player to a station and man it. Movement between
// stations is the game, but nobody should be lost on their own ship.
function teleportTo(i) {
  const st = G.ship.stations[i];
  if (!st || G.over) return;
  G.hud.toggleTeleport(false);
  G.stations.exit();
  player.pos.set(st.pos.x, st.pos.y + 0.02, st.pos.z);
  player.vel.set(0, 0, 0);
  player.onLadder = false;
  G.stations.enter(st);
  G.hud.log(`LAID TO ${st.name}`);
}

// Teleport into a compartment: find the clear standing spot nearest the
// room's center (furniture and fittings make the exact center a gamble).
function teleportToRoom(r) {
  if (!r || G.over) return;
  const g = G.ship.grid;
  const cx = (r.x0 + r.x1) / 2, cz = (r.z0 + r.z1) / 2;
  let best = null, bd = Infinity;
  for (let x = r.x0; x <= r.x1; x++) {
    for (let z = r.z0; z <= r.z1; z++) {
      if (g.solid(x, r.walk, z) || g.solid(x, r.walk + 1, z)) continue;
      if (!g.solid(x, r.walk - 1, z)) continue; // needs a deck underfoot
      const d = Math.hypot(x + 0.5 - cx, z + 0.5 - cz);
      if (d < bd) { bd = d; best = [x, z]; }
    }
  }
  if (!best) return;
  G.hud.toggleTeleport(false);
  G.stations.exit();
  player.pos.set(best[0] + 0.5, r.walk + 0.02, best[1] + 0.5);
  player.vel.set(0, 0, 0);
  player.onLadder = false;
  player.yaw = Math.atan2(cz - player.pos.z, cx - player.pos.x) || 0;
  G.hud.log(`PIPED BELOW — ${r.name}`);
}

// --- input --------------------------------------------------------------------
// The context-sensitive USE: back aboard from the salvo cam, or man whatever
// station is at hand. Shared by the E key, a tap on the prompt, and a tap on
// the world itself (touch.js).
function useAction() {
  if (shellCam.on) { toggleShellCam(); return; }
  const st = G.stations.nearest();
  if (st) G.stations.enter(st);
}

// One router for every "pressed key" no matter who pressed it — keyboard or
// a touch chip — so the precedence (teleport menu, then stations, then the
// world) can never drift between the two.
function routeKey(code, fromKeyboard) {
  // teleport menu owns the keyboard while it's up
  if (G.hud.tpOpen) {
    if (/^Digit[1-9]$/.test(code)) { teleportTo(+code.slice(5) - 1); return; }
    if (code === "KeyT" || code === "Escape" || code === "KeyE") { G.hud.toggleTeleport(false); return; }
    const room = G.hud.tpRoomKeys?.[code];
    if (room) { teleportToRoom(room); return; }
    return;
  }
  // stations get first crack
  if (G.stations.onKey(code)) return;
  if (fromKeyboard) player.keys.add(code);
  switch (code) {
    case "KeyE": useAction(); break;
    case "KeyM": G.hud.toggleMap(); break;
    case "BracketLeft": G.hud.mapLevelShift(-1); break;
    case "BracketRight": G.hud.mapLevelShift(1); break;
    case "KeyH": G.hud.toggleHelp(); break;
    case "KeyG": soundGQ(!G.gq); break;
    case "KeyT": if (started) G.hud.toggleTeleport(); break;
    case "KeyV": if (started) toggleShellCam(); break;
    case "KeyI": if (started) G.illum.fire(); break;
  }
}
G.routeKey = routeKey;
G.useAction = useAction;
G.teleportTo = teleportTo;
G.teleportToRoom = teleportToRoom;

document.addEventListener("keydown", (e) => {
  if (["Tab", "Space", "BracketLeft", "BracketRight"].includes(e.code)) e.preventDefault();
  if (e.repeat) { player.keys.add(e.code); return; }
  routeKey(e.code, true);
});
document.addEventListener("keyup", (e) => player.keys.delete(e.code));
document.addEventListener("mousemove", (e) => {
  if (document.pointerLockElement !== canvas) return;
  if (G.stations.active?.kind === "director") {
    // slew the director with the mouse
    G.stations.viewYaw += e.movementX * 0.0006 / (player.zoom / 8);
  } else player.look(e.movementX, e.movementY);
});
canvas.addEventListener("mousedown", (e) => {
  if (G.touch) return; // gestures live in touch.js; no pointer lock on glass
  if (document.pointerLockElement !== canvas) { if (started) canvas.requestPointerLock(); return; }
  if (e.button === 2 && !G.stations.active) player.zoom = 7;
});
document.addEventListener("mouseup", (e) => {
  if (e.button === 2 && !G.stations.active) player.zoom = 1;
});
document.addEventListener("contextmenu", (e) => e.preventDefault());

// The touch layer: joystick, look/pinch gestures, chips, haptics. A no-op
// with a mouse — G.touch stays false and desktop input runs exactly as above.
initTouch(G);

// --- resize -------------------------------------------------------------------
function resize() {
  renderer.setSize(window.innerWidth, window.innerHeight, false);
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
}
window.addEventListener("resize", resize);
resize();

// --- helpers ------------------------------------------------------------------
const freeCam = { on: false, pos: new THREE.Vector3(), target: new THREE.Vector3(), fov: 55 };
const tmpV = new THREE.Vector3();
let smokeAcc = 0, sprayAcc = 0, uiAcc = 0, secAcc = 0;

function updatePrompt() {
  // On glass the prompt IS the button (touch.js wires it), so the label
  // names the gesture instead of a key.
  const T = G.touch;
  if (shellCam.on) { G.hud.setPrompt(T ? "TAP — BACK ABOARD" : "<b>V</b> — BACK ABOARD"); return; }
  if (G.stations.active || G.over) { G.hud.setPrompt(null); return; }
  const st = G.stations.nearest();
  if (st) { G.hud.setPrompt(T ? `TAP — MAN ${st.name}` : `<b>E</b> — MAN ${st.name}`); return; }
  // fire fighting prompt
  for (const c of G.damage.comps) {
    if (c.fire > 0.02 && G.damage.playerNear(c, 3)) {
      G.hud.setPrompt(T ? `HOLD — FIGHT THE FIRE (${c.name})` : `<b>HOLD E</b> — FIGHT THE FIRE (${c.name})`);
      G.playerDousing = player.keys.has("KeyE");
      G.playerDousingC = c;
      return;
    }
  }
  G.playerDousing = false;
  // A thumb can't hold SHIFT to reach for a trunk: standing over a ladder
  // gets its own hold-prompt, which touch.js turns into the same
  // SHIFT-while-slow grab the desktop uses.
  if (T && !player.onLadder && player.onGround && player.touchingLadder(0.35)) {
    G.hud.setPrompt("HOLD — TAKE THE LADDER", { ladder: true });
    return;
  }
  G.hud.setPrompt(null);
}

// --- main loop ------------------------------------------------------------------
let last = performance.now();
const debug = { timeScale: 1 };
function frame(now) {
  requestAnimationFrame(frame);
  let dt = Math.min(0.1, (now - last) / 1000) * debug.timeScale;
  last = now;
  if (!started) { renderer.render(scene, camera); return; }
  // salvo-camera cruise phase runs the world hot so the ride stays watchable
  if (shellCam.on && shellCam.shell && !shellCam.shell.dead && shellCam.shell.pos.y > 160) dt *= 2.5;

  // ship kinematics
  const s = shipState;
  if (!s.rudderJammed) s.rudder += Math.max(-1, Math.min(1, s.orderedRudder - s.rudder)) * dt * 0.4;
  const answer = 0.03 * (s.engineManned > 0 ? 2 : 1);
  s.engineManned = Math.max(0, s.engineManned - dt);
  const cap = (s.speedCap ?? 33) * KTS;
  s.speed += Math.max(-1, Math.min(1, Math.min(s.orderedSpeed, cap) - s.speed)) * dt * answer * 10;
  s.speed = Math.max(0, Math.min(s.speed, cap));
  if (s.speed > 0.3) s.heading += (s.rudder / 0.61) * (s.speed / 420) * dt;
  s.pos.x += Math.sin(s.heading) * s.speed * dt;
  s.pos.y += Math.cos(s.heading) * s.speed * dt;

  s.recoil = Math.max(0, s.recoil - dt * 0.08);
  shake = Math.max(0, shake - dt * 2.2);
  const seaRoll = Math.sin(now / 4700) * 0.012 + Math.sin(now / 1900) * 0.004;
  let sinkY = 0;
  if (s.sinking) {
    s.sinkT = (s.sinkT || 0) + dt;
    sinkY = -Math.min(18, s.sinkT * 0.4);
    s.list += dt * 0.01;
  }
  shipGroup.position.set(s.pos.x, sinkY, s.pos.y);
  shipGroup.rotation.order = "YXZ";
  shipGroup.rotation.y = s.heading - Math.PI / 2;
  shipGroup.rotation.x = seaRoll + s.list + s.recoil * Math.sin(now / 90);
  shipGroup.rotation.z = s.trim;
  shipGroup.updateMatrixWorld();

  // systems
  player.update(dt);
  G.stations.update(dt, player.keys);
  G.guns.update(dt);
  G.fc.update(dt);
  G.radar.update(dt);
  G.damage.update(dt);
  G.crew.update(dt);
  for (const e of G.enemyShips) e.update(dt);
  for (const t of G.shore) t.update(dt);
  G.mission?.update(G, dt);
  G.worldFx.update(dt);
  G.shipFx.update(dt);
  G.hud.update(dt);
  G.waypoint.update(dt);
  G.plotBoard.update(dt);
  G.illum.update(dt);
  G.torps.update(dt);
  G.barks.update(dt);

  // radar antennas spin
  ship.skRadar.rotation.y += dt * 0.8;
  ship.sgRadar.rotation.y = G.radar.sweep ? -G.radar.sweep + Math.PI / 2 : ship.sgRadar.rotation.y + dt;

  // turrets follow fc order visuals already; director housing follows viewYaw when manned,
  // otherwise tracks the target on its own
  if (G.stations.active?.kind !== "director" && G.fc.target) {
    const rel = Math.atan2(
      Math.sin(G.fc.targetBearing() - s.heading), Math.cos(G.fc.targetBearing() - s.heading));
    ship.directorMesh.rotation.y = -rel;
    ship.aftDirectorMesh.rotation.y = -rel;
  }

  // funnel smoke + bow spray
  smokeAcc += dt;
  if (smokeAcc > 0.2) {
    smokeAcc = 0;
    for (const f of ship.funnelTops) {
      tmpV.copy(f);
      shipInner.localToWorld(tmpV);
      funnelSmoke(G.worldFx, tmpV, new THREE.Vector3(Math.sin(s.heading) * s.speed, 0, Math.cos(s.heading) * s.speed));
    }
  }
  // secondary battery: the 5"/38s open up on their own once a target is
  // inside their envelope — pure theater, but the ship should feel alive
  secAcc += dt;
  if (G.gq && secAcc > 2.2 && !G.over) {
    secAcc = 0;
    const p = G.shipWorldPos();
    const close = G.targets().find((e) => !e.sunk && Math.hypot(e.pos.x - p.x, e.pos.y - p.z) < 16000);
    if (close && Math.random() < 0.5) {
      const mount = ship.fives[(Math.random() * ship.fives.length) | 0];
      tmpV.copy(mount); shipInner.localToWorld(tmpV);
      const b = Math.atan2(close.pos.x - tmpV.x, close.pos.y - tmpV.z);
      fx.muzzleBlast(G.worldFx, tmpV, new THREE.Vector3(Math.sin(b) * 0.6, 0.25, Math.cos(b) * 0.6));
      G.audio.secondary();
    }
  }

  sprayAcc += dt;
  if (s.speed > 3 && sprayAcc > 0.1) {
    sprayAcc = 0;
    tmpV.set(262, WATERLINE_Y + 0.5, 17.5);
    shipInner.localToWorld(tmpV);
    bowSpray(G.worldFx, tmpV, Math.random() < 0.5 ? -1 : 1);
  }

  // scenario progression: generic order checks + objective waypoint
  uiAcc += dt;
  if (uiAcc > 0.5) {
    uiAcc = 0;
    updatePrompt();
    for (const o of orders) {
      if (!o.done && o.check?.(G)) G.hud.completeOrder(o.id);
    }
    const next = orders.find((o) => !o.done && o.anchor);
    const cur = orders.find((o) => !o.done);
    G.waypoint.set(cur === next && next ? next.anchor : null, next?.label);
    if (player.overboard) {
      player.overboard = false;
      G.hud.log("MAN OVERBOARD RECOVERED (we won't log it if you won't)", "ok");
    }
    G.playerDousing = player.keys.has("KeyE") && G.playerDousingC && G.damage.playerNear(G.playerDousingC, 3);
  }

  // camera
  if (shellCam.on) {
    const sh = shellCam.shell;
    if (camera.parent !== scene) scene.add(camera);
    if (!sh.dead) {
      tmpV.copy(sh.vel).normalize();
      camera.position.copy(sh.pos).addScaledVector(tmpV, -26);
      camera.position.y += 10;
      camera.lookAt(sh.pos.x + tmpV.x * 60, sh.pos.y + tmpV.y * 60, sh.pos.z + tmpV.z * 60);
    } else {
      shellCam.linger -= dt;
      camera.lookAt(sh.pos); // parked where the ride ended, watching the burst
      if (shellCam.linger <= 0) shellCam.on = false;
    }
    camera.fov = 58;
  } else if (freeCam.on) {
    if (camera.parent !== scene) scene.add(camera);
    camera.position.copy(freeCam.pos);
    camera.lookAt(freeCam.target);
    camera.fov = freeCam.fov;
  } else {
    if (camera.parent !== shipInner) shipInner.add(camera);
    camera.position.copy(G.stations.eyeLocal ?? player.eyePos());
    if (shake > 0.01) {
      camera.position.x += (Math.random() - 0.5) * 0.06 * shake;
      camera.position.y += (Math.random() - 0.5) * 0.06 * shake;
      camera.position.z += (Math.random() - 0.5) * 0.06 * shake;
    }
    camera.rotation.set(0, 0, 0);
    camera.rotation.order = "YXZ";
    camera.rotation.y = -player.yaw - Math.PI / 2;
    camera.rotation.x = player.pitch;
    camera.fov = 72 / player.zoom;
  }
  camera.updateProjectionMatrix();

  // binocular vignette
  document.getElementById("bb-binoc").classList.toggle("hidden", player.zoom < 3);
  document.getElementById("bb-zoom").classList.toggle("hidden", player.zoom < 3);
  if (player.zoom >= 3) document.getElementById("bb-zoom").textContent = `${player.zoom.toFixed(0)}×`;

  camera.getWorldPosition(tmpV);
  sea.update(dt, tmpV);
  // The sea is a plane at y=0 and makes no promise about staying out of the
  // hull: the third deck lies below the waterline, and the disc would paint
  // open water straight across the magazine floor. A covered eye below the
  // second deck has no honest sightline to the water — douse the disc and
  // give the compartments their deck plates back.
  if (shellCam.on || freeCam.on) {
    sea.sea.visible = true;
  } else {
    const eye = G.stations.eyeLocal ?? player.eyePos();
    sea.sea.visible = !(eye.y < 13 && ship.grid.underCover(eye.x, eye.y, eye.z));
  }
  renderer.render(scene, camera);
}
requestAnimationFrame(frame);

// Debug hook for Playwright-driven tests and staged screenshots.
window.__bb = {
  G, scene, camera, player, ship, shipState, shipGroup, shipInner, THREE,
  freeCam, start, debug, MISSIONS, teleportTo, teleportToRoom,
};
